import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import errorHandler from 'Utils/error-handler';

import LoginFormSchema from './login-form.validation';
import { LockFilled, MailFilled } from '@ant-design/icons';
import { Space, Button as ButtonAntd, Radio } from 'antd';
import { AlertBox, Button, TextInput, Password, RadioGroup } from '@life-line-lab/components-react';
import { UserOutlined, ShoppingOutlined } from '@ant-design/icons';
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

import './login-form.scss';

interface LoginFormProps {
  status?: string;
  expectedError?: any,
  onSuccess?: () => void,
  onSubmit: (input: {
    email: string,
    password: string,
    accountType: string,
  }) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ status, expectedError, onSuccess, onSubmit }) => {
  const initialInput = {
    accountType: 'INDIVIDUAL_ACCOUNT',
    email: '',
    password: '',
  }

  const initialError = { ...initialInput, general: '' }

  const { t } = useTranslation();
  const history = useHistory();
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);


  useEffect(() => {
    localStorage.removeItem('CHAT_TOKEN')
  }, [])

  useEffect(() => {
    if (status === 'error' && !isEmpty(expectedError)) {
      if (expectedError.value && expectedError.value.trim() !== "") {
        setError({ ...error, general: t(expectedError.value) });
      } else {
        setError({ ...error, general: t("error.emailPasswordIncorrect") });
      }
    }
    else if (status === 'success') {
      onSuccess && onSuccess();
    }
  }, [status]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    try {
      const value = LoginFormSchema.validateSync(input, {
        strict: true,
        abortEarly: true,
        stripUnknown: false
      });

      if (!isEmpty(value))
        onSubmit(input);
    }
    catch (err) {
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      }
      else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  }

  return (

    <div className="login-form">
      <form>
        <Space className="login-form--inner" direction="vertical" size="large">
          <AlertBox isFilled={true} text={error.general} />

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', gap: 24 }}>
            <span style={{ whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{t('label.loginAs')}</span>
            <Radio.Group
              isRequired={true}
              name="accountType"
              value={input.accountType}
              direction='horizontal'
              onChange={handleChange}
              optionType="button"
              options={[
                { value: 'INDIVIDUAL_ACCOUNT', label: (
                  <div style={{display: 'flex', gap: 8, alignItems: 'center' }}>
                    <UserOutlined /> {t('label.individual')}
                  </div>) },
                { value: 'BUSINESS_ACCOUNT', label: (
                  <div style={{display: 'flex', gap: 8, alignItems: 'center' }}>
                    <HiOutlineOfficeBuilding /> {t('label.business')}
                  </div>
                ) },
              ]}
            />
          </div>

          <div>
            <TextInput
              isRequired={true}
              // label={t('label.email')}
              className='input-field'
              name="email"
              placeholder={t('label.email')}
              prefix={<MailFilled />}
              value={input.email}
              onChange={handleChange}
            />
            <AlertBox text={error.email} />
          </div>

          <div>
            <Password
              isRequired={true}
              prefix={<LockFilled />}
              name="password"
              className='input-field'
              placeholder={t('label.password')}
              value={input.password}
              onChange={handleChange}
              onPressEnter={handleSubmit}
            />
            <AlertBox text={error.password} />
          </div>

          <Space className="login-form--actions" direction="vertical" size="large">
            <Link to="/forget-password">
              {`${t('action.forgetPassword')}`}
            </Link>

            <Button
              isLoading={status === "loading"}
              type="primary"
              className='button'
              label={t('action.login')}
              size="large"
              onClick={handleSubmit}
            />
          </Space>

          <Space direction="vertical" size="middle">
            <span
              className="pt-10"
              dangerouslySetInnerHTML={{
                __html: t('txt.signUp', {
                  signUpLink: '/register'
                })
              }}
            />

            <span
              className="pt-20"
              dangerouslySetInnerHTML={{
                __html: t('txt.agreementConsent', {
                  termsLink: '/terms',
                  privacyLink: '/privacy'
                })
              }}
            />
          </Space>
        </Space>
      </form>
    </div>
  )
}

export default LoginForm;
